import { NumberInput } from '@mantine/core'
import { formatClock } from '@racedash/shared'
import { formatDuration } from '@racedash/shared'
import type { MessageDoc } from '@racedash/shared'
import { Tile } from '../Tile'
import type { WidgetEditorProps, WidgetProps } from './props'

/**
 * When a message was said, on the clock on the wall.
 *
 * This used to show race elapsed time, on the reasoning that "1:12:04" is where
 * in the race something happened and that is how a log gets read afterwards.
 * The reasoning was fine and the display was not: formatDuration writes twenty-
 * one minutes as "21:43", which is indistinguishable from twenty to ten at
 * night. A reader has no way to tell which they are looking at, and every other
 * time on this app — the stint chart's axis, the planner's windows, the session
 * times — is time of day.
 *
 * So time of day it is, with the race time kept on the title where it can be
 * asked for rather than guessed at.
 *
 * `at` first: that is when race control issued it. receivedAt is our own poll
 * and a second or two later, and is all there is for anything captured before
 * the vendor's own stamp was stored.
 */
function when(m: MessageDoc): string {
  const at = m.at?.toDate() ?? m.receivedAt?.toDate()
  if (!at) return '—'
  return formatClock(at, { seconds: true })
}

/** The race time, for the hover — "where in the race" is still a real question. */
function intoRace(m: MessageDoc): string | undefined {
  if (m.elapsedMs == null || m.elapsedMs <= 0) return undefined
  return `${formatDuration(m.elapsedMs)} into the session`
}

export default function Messages({ config, data }: WidgetProps<'messages'>) {
  const messages = data.messages.slice(0, config.rows)

  if (messages.length === 0) {
    return (
      <Tile label="Race control">
        <span className="dash-dead-text">Nothing said yet</span>
      </Tile>
    )
  }

  return (
    <Tile label="Race control">
      <div className="dash-list">
        {messages.map((m, i) => (
          // The vendor's id is not carried through onto the document, so the
          // position in a newest-first list is the only stable key available.
          <div className="dash-list-row" key={`${when(m)}-${i}`}>
            <span className="dash-list-when" title={intoRace(m)}>
              {when(m)}
            </span>
            <span className="dash-list-what" title={m.message}>
              {m.message}
            </span>
          </div>
        ))}
      </div>
    </Tile>
  )
}

export function Editor({ config, onChange }: WidgetEditorProps<'messages'>) {
  return (
    <NumberInput
      label="Messages to show"
      description="Older ones are still captured; this is only how many fit on the tile."
      min={1}
      max={40}
      value={config.rows}
      onChange={(v) => onChange({ kind: 'messages', rows: typeof v === 'number' ? v : config.rows })}
    />
  )
}
